import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Radio, RefreshCw, Wifi, WifiOff, Send, Activity, MapPin, Server, Zap, Globe, Terminal,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../components/ui/use-toast';
import { useRestaurant } from '../context/RestaurantContext';
import { useT } from '../context/LanguageContext';
import apiClient from '../services/api';
import { subscribeRealtime } from '../lib/realtime';

/* Realtime dashboard: health, connections, throughput, a live event feed and a test broadcast; otherwise read-only. */

const APP_LABEL = { pos: 'POS', kitchen: 'Kitchen', web: 'Web app', customer: 'Customer' };
const APP_CLS = {
  pos: 'bg-blue-100 text-blue-700',
  kitchen: 'bg-orange-100 text-orange-700',
  web: 'bg-violet-100 text-violet-700',
  customer: 'bg-emerald-100 text-emerald-700',
};

export default function RealtimePage() {
  const { restaurantId } = useRestaurant();
  const { toast } = useToast();
  const { t } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [wsLive, setWsLive] = useState(false);
  const [wsRefused, setWsRefused] = useState(false);
  // The socket never opened and the client stopped trying - a different answer, and a different fix, from a bad token.
  const [wsDown, setWsDown] = useState(false);
  const [feed, setFeed] = useState([]);
  const [testArea, setTestArea] = useState('reservations');
  const [sending, setSending] = useState(false);
  const feedSeq = useRef(0);

  /* The operator's SSH checklist, run by the server and shown here - for the
     day the tile says "Worker down" and the reason is in a journal nobody can
     reach. Started by hand, never on a timer: it spawns a throwaway Daphne. */
  const [diag, setDiag] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);
  const runDiagnostics = async () => {
    setDiagBusy(true);
    try {
      const { data: res } = await apiClient.post(`/users/restaurant/${restaurantId}/realtime/diagnostics/`);
      if (!res?.success) throw new Error(res?.error || 'failed');
      setDiag(res.data);
    } catch (e) {
      setDiag({ error: e?.response?.data?.error || e.message });
    } finally {
      setDiagBusy(false);
    }
  };

  const load = useCallback(async () => {
    if (!restaurantId) return;
    try {
      const { data: res } = await apiClient.get(`/users/restaurant/${restaurantId}/realtime/status/`);
      if (!res?.success) throw new Error(res?.error || 'Failed');
      setData(res.data);
      setError('');
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  // Live event feed: this page is itself a WS client, so every event it receives confirms the link.
  useEffect(() => {
    const unsub = subscribeRealtime(
      ['reservations', 'kitchen', 'order', 'test', 'connected', 'disconnected',
        'refused', 'unavailable'],
      (msg) => {
        /* 'connected' arrives on a fresh socket or is replayed when joining one another screen opened; 'disconnected' is client-side. */
        if (msg.kind === 'connected') {
          setWsLive(true); setWsRefused(false); setWsDown(false); return;
        }
        if (msg.kind === 'disconnected') { setWsLive(false); return; }
        /* The handshake never succeeded, so the tile says /ws/ isn't reaching Daphne rather than blaming the token. */
        if (msg.kind === 'unavailable') { setWsLive(false); setWsDown(true); return; }
        /* The server rejected this token and the client gave up, which beats a tile reading "Connecting..." forever. */
        if (msg.kind === 'refused') { setWsLive(false); setWsRefused(true); return; }
        feedSeq.current += 1;
        const at = new Date();
        setFeed((f) => [{ id: feedSeq.current, at, ...msg }, ...f].slice(0, 40));
      },
      { app: 'web' },
    );
    return () => unsub();
  }, []);

  const send = async () => {
    setSending(true);
    try {
      const { data: res } = await apiClient.post(
        `/users/restaurant/${restaurantId}/realtime/test/`,
        { area: testArea, message: 'Test from Realtime dashboard' });
      if (!res?.success) throw new Error(res?.error || 'Failed');
      toast({ title: 'Test sent', description: res.message });
    } catch (e) {
      toast({ title: 'Failed to send', description: e?.response?.data?.error || e.message, variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const maxEvt = useMemo(
    () => Math.max(1, ...((data?.event_series || []).map((s) => s.count))),
    [data],
  );

  const redisOk = data?.redis_available;

  /* Measured ON THE SERVER, never here. The API sends a real WebSocket
     handshake to the server's own nginx - with the same Host header this
     browser arrived on - and reports what came back. It is the one reading
     that can tell a stopped worker from a proxy that answers /ws/ with the
     site, and it is read BEFORE the port bookkeeping: whether the upgrade
     was accepted settles more than which port the process was started on. */
  const wsHop = useMemo(() => {
    const tr = data?.transport;
    if (!tr) return { value: '—', tone: undefined, sub: t('realtime.hop.none') };
    const port = tr.daphne_port;
    const found = tr.daphne_found_port;
    const addr = tr.daphne_bound || `127.0.0.1:${port}`;
    if (tr.daphne_listening === false) {
      const tried = (tr.daphne_tried || [`127.0.0.1:${port}`]).join(', ');
      return { value: t('realtime.hop.workerDown'), tone: 'bad', sub: t('realtime.hop.workerDownSub', { tried }) };
    }
    const verdict = tr.ws_route?.verdict;
    const code = tr.ws_route?.code ?? '—';
    if (verdict === 'ok') {
      // Sockets work. Through a process other than the dedicated worker,
      // say so without shouting: a note, not an outage.
      if (found && found !== port) {
        return { value: t('realtime.hop.viaPort', { found }), tone: 'warn', sub: t('realtime.hop.viaPortSub', { addr, code, port, found }) };
      }
      return { value: t('realtime.hop.ok'), tone: 'ok', sub: t('realtime.hop.okSub', { addr, code }) };
    }
    if (verdict === 'spa') return { value: t('realtime.hop.notRouted'), tone: 'bad', sub: t('realtime.hop.notRoutedSub', { code }) };
    if (verdict === 'daphne_down') return { value: t('realtime.hop.workerDown'), tone: 'bad', sub: t('realtime.hop.routedNoAnswerSub', { code, port }) };
    if (verdict === 'unreachable') return { value: t('realtime.hop.noAnswer'), tone: 'bad', sub: t('realtime.hop.noAnswerSub') };
    if (found && found !== port) {
      // Something accepts TCP on another port but did not take the upgrade:
      // the plain HTTP server, not a WebSocket worker.
      return { value: t('realtime.hop.wrongPort'), tone: 'bad', sub: t('realtime.hop.wrongPortSub', { found, port, code }) };
    }
    return { value: t('realtime.hop.unexpected'), tone: 'warn', sub: t('realtime.hop.unexpectedSub', { code }) };
  }, [data, t]);

  /* The hop the tile above cannot see - checked from HERE, because this
     browser is on the public side of every proxy and the server is not (it
     cannot reach its own public hostname from inside, so a server-side probe
     always read "No answer"). A plain GET to each socket path, no Upgrade
     header: the application tier answers a 4xx (route exists, upgrade
     required), a dead worker behind a routing proxy answers 502, and a proxy
     serving the website for that path answers 200 with HTML - the fault this
     tile exists to name. Both doors are tried, because the clients try both. */
  const [pubProbe, setPubProbe] = useState(null);
  useEffect(() => {
    let alive = true;
    const classify = (r) => {
      const ct = r.headers.get('content-type') || '';
      if (r.status === 200 && ct.includes('text/html')) return 'spa';
      if ([502, 503, 504].includes(r.status)) return 'daphne_down';
      if ([400, 403, 404, 426].includes(r.status)) return 'routed';
      return 'unexpected';
    };
    (async () => {
      const out = {};
      for (const path of ['/ws/notifications/', '/api/ws/notifications/']) {
        try {
          const r = await fetch(`${path}?token=browser-probe`, { cache: 'no-store' });
          out[path] = { status: r.status, verdict: classify(r) };
        } catch (e) {
          out[path] = { status: null, verdict: 'unreachable' };
        }
      }
      if (alive) setPubProbe(out);
    })();
    return () => { alive = false; };
  }, [data]);

  const publicHop = useMemo(() => {
    const host = window.location.host;
    // A socket that opened - or that the server refused by token - crossed
    // every proxy: that is the whole question, answered.
    if (wsLive || wsRefused) return { value: t('realtime.pub.open'), tone: 'ok', sub: t('realtime.pub.openSub', { host }) };
    if (!pubProbe) return { value: '—', tone: undefined, sub: t('realtime.pub.checking') };
    const ws = pubProbe['/ws/notifications/'] || {};
    const api = pubProbe['/api/ws/notifications/'] || {};
    const tier = {
      routed: t('realtime.pub.tierApp'), spa: t('realtime.pub.tierSite'), daphne_down: t('realtime.pub.tierDown'),
      unreachable: t('realtime.pub.tierNone'), unexpected: t('realtime.pub.tierOdd'),
    };
    const say = (p, r) => `${p} → ${tier[r.verdict] || r.verdict} (HTTP ${r.status ?? '—'})`;
    const both = `${say('/ws/', ws)} · ${say('/api/ws/', api)}`;
    const routed = ws.verdict === 'routed' || api.verdict === 'routed';
    // Plain requests get through and the socket still never opened: the
    // proxy forwards the request but not its Upgrade header. The plain probe
    // alone cannot see this; the fate of this page's own socket can.
    if (routed && wsDown) return { value: t('realtime.pub.stripped'), tone: 'bad', sub: t('realtime.pub.strippedSub', { both, host }) };
    if (ws.verdict === 'routed') return { value: t('realtime.pub.routed'), tone: 'ok', sub: t('realtime.pub.routedSub', { both }) };
    if (api.verdict === 'routed') return { value: t('realtime.pub.viaApi'), tone: 'warn', sub: t('realtime.pub.viaApiSub', { both, host }) };
    if (ws.verdict === 'spa' || api.verdict === 'spa') return { value: t('realtime.pub.frontProxy'), tone: 'bad', sub: t('realtime.pub.frontProxySub', { both, host }) };
    if (ws.verdict === 'daphne_down' || api.verdict === 'daphne_down') return { value: t('realtime.hop.workerDown'), tone: 'bad', sub: t('realtime.pub.downSub', { both }) };
    if (ws.verdict === 'unreachable' && api.verdict === 'unreachable') return { value: t('realtime.hop.noAnswer'), tone: 'warn', sub: t('realtime.pub.noneSub', { host }) };
    return { value: t('realtime.hop.unexpected'), tone: 'warn', sub: t('realtime.pub.oddSub', { both }) };
  }, [pubProbe, wsLive, wsRefused, wsDown, t]);
  const time = (d) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  return (
    <div className="p-6 w-full">
      <header className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight leading-none">Realtime notifications</h1>
        </div>
        <button onClick={load}
          className="inline-flex items-center gap-2 rounded-md border border-border bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
          <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </header>

      {/* Two tiles are read from this browser, everything else on the
          server - said up front, because a page full of ports and process
          ids was read as "the WebSockets are running on my PC". */}
      <p className="mb-4 text-xs text-muted-foreground">{t('realtime.where')}</p>

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">{error}</div>
      )}

      {/* Health tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 mb-4">
        <StatTile icon={wsLive ? Wifi : WifiOff}
          label="This browser's socket"
          value={wsLive ? 'Connected' : wsRefused ? 'Refused' : wsDown ? 'Unreachable' : 'Connecting…'}
          tone={wsLive ? 'ok' : (wsRefused || wsDown) ? 'bad' : 'warn'}
          sub={wsRefused
            ? 'The server rejected this sign-in for the socket — screens are polling instead.'
            : wsDown
              ? 'The socket never opened, so the client stopped trying. /ws/ is not reaching the '
                + 'Daphne process — check it is running and that every proxy in front of it '
                + 'forwards the Upgrade header. Screens are polling instead.'
              : (!wsLive ? 'No socket yet. If this never turns green, /ws/ is not reaching the '
                + 'Daphne process (check the proxy passes WebSocket upgrades).' : null)} />
        <StatTile icon={Server} label="Channel layer (Redis)"
          value={redisOk ? 'Up' : 'Unavailable'} tone={redisOk ? 'ok' : 'bad'}
          sub={redisOk && data?.redis_latency_ms != null ? `${data.redis_latency_ms} ms round-trip` : null} />
        {/* The server's own verdict, which the browser cannot give: it probes
            this box's nginx with a real upgrade, using the same Host header
            the caller arrived on, and says which hop broke. */}
        <StatTile icon={Radio} label={t('realtime.tile.server')}
          value={wsHop.value} tone={wsHop.tone} sub={wsHop.sub} />
        {/* Every hop, from the public hostname in: the one that fails while
            all of the above stays green. */}
        <StatTile icon={Globe} label={t('realtime.tile.frontProxy')}
          value={publicHop.value} tone={publicHop.tone} sub={publicHop.sub} />
        <StatTile icon={Activity} label="Connected clients"
          value={data?.total_connections ?? '—'} />
        <StatTile icon={Zap} label="Events / sec (last min)"
          value={data ? data.events_per_sec : '—'}
          sub={data ? `${data.events_last_minute} in last minute` : null} />
      </div>

      {/* Named, with the command that fixes it: the difference between this
          and a red tile is whether somebody has to go and read a runbook. */}
      {wsHop.tone === 'bad' && !loading && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-800">
          <strong>Realtime is off: {wsHop.value.toLowerCase()}.</strong> {wsHop.sub}
          {' '}Every screen is polling over REST instead, which is why order and kitchen
          updates arrive late rather than not at all.
        </div>
      )}
      {publicHop.tone === 'bad' && !loading && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-800">
          <strong>Realtime is off for every till and phone: {publicHop.value.toLowerCase()}.</strong>{' '}
          {publicHop.sub}{' '}Fixing this server alone will not bring the sockets back.
        </div>
      )}

      {!redisOk && !loading && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
          The Redis channel layer isn't reachable, so realtime push is off and clients are falling back to polling.
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
        {/* Connections by location */}
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold flex items-center gap-2"><MapPin size={15} className="text-primary" /> Connected clients</h3>
              <div className="flex gap-1.5">
                {Object.entries(data?.by_app || {}).map(([app, n]) => (
                  <span key={app} className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${APP_CLS[app] || 'bg-secondary'}`}>
                    {APP_LABEL[app] || app} · {n}
                  </span>
                ))}
              </div>
            </div>
            {(!data || data.locations.length === 0) ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No clients connected right now.</p>
            ) : (
              <div className="space-y-3">
                {data.locations.map((loc) => (
                  <div key={String(loc.location_id)} className="rounded-lg border border-border p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">
                        {loc.location_id ? `Location #${loc.location_id}` : 'Unspecified location'}
                      </span>
                      <span className="text-xs text-muted-foreground">{loc.count} client(s)</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {loc.clients.map((c, i) => (
                        <span key={i} className={`px-2 py-0.5 rounded-md text-[11px] font-medium ${APP_CLS[c.app] || 'bg-secondary'}`}
                          title={`idle ${c.idle_seconds}s${c.staff_id ? ` · staff ${c.staff_id}` : ''}${c.customer_id ? ` · customer ${c.customer_id}` : ''}`}>
                          {APP_LABEL[c.app] || c.app || 'client'}
                          {c.idle_seconds > 0 ? ` · ${c.idle_seconds}s` : ''}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Throughput + test */}
        <div className="space-y-4">
          <Card>
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold flex items-center gap-2 mb-3"><Activity size={15} className="text-primary" /> Events / minute (last 15 min)</h3>
              <div className="flex items-end gap-1 h-24">
                {(data?.event_series || []).map((s) => (
                  <div key={s.minute} className="flex-1 bg-primary/15 rounded-t relative group" style={{ height: `${(s.count / maxEvt) * 100}%`, minHeight: s.count ? 3 : 1 }}
                    title={`${s.count} event(s)`}>
                    <span className="absolute -top-4 left-1/2 -translate-x-1/2 text-[10px] text-muted-foreground opacity-0 group-hover:opacity-100">{s.count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold flex items-center gap-2 mb-3"><Send size={15} className="text-primary" /> Send a test</h3>
              <div className="flex items-center gap-2">
                <select value={testArea} onChange={(e) => setTestArea(e.target.value)}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm">
                  <option value="reservations">Reservations group</option>
                  <option value="kitchen">Kitchen group</option>
                </select>
                <button onClick={send} disabled={sending}
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground text-sm font-medium px-4 py-2 hover:bg-primary/90 disabled:opacity-60">
                  <Send size={14} /> {sending ? 'Sending…' : 'Send test'}
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Live event feed */}
      <Card className="mt-4">
        <CardContent className="p-5">
          <h3 className="text-sm font-semibold flex items-center gap-2 mb-3"><Radio size={15} className="text-primary" /> Live event feed</h3>
          {feed.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Waiting for events… (create a booking or send a test)</p>
          ) : (
            <div className="space-y-1 max-h-80 overflow-y-auto font-mono text-xs">
              {feed.map((f) => (
                <div key={f.id} className="flex items-center gap-3 py-1 border-b border-border/50">
                  <span className="text-muted-foreground shrink-0">{time(f.at)}</span>
                  <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary font-semibold shrink-0">{f.kind}</span>
                  <span className="text-foreground/80 truncate">
                    {Object.entries(f).filter(([k]) => !['id', 'at', 'kind'].includes(k))
                      .map(([k, v]) => `${k}=${v}`).join('  ')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Server diagnostics */}
      <Card className="mt-4">
        <CardContent className="p-5">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Terminal size={15} className="text-primary" /> Server diagnostics
            </h3>
            <button type="button" onClick={runDiagnostics} disabled={diagBusy}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60">
              <RefreshCw size={14} className={diagBusy ? 'animate-spin' : ''} />
              {diagBusy ? 'Running on the server…' : 'Run server diagnostics'}
            </button>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Runs on the server as the web user: the unit&apos;s state and restart count, what is on port
            8001, an import of the ASGI app, a few-second dry-run of Daphne on a spare port (which
            reproduces a crash with its exact error), and the unit&apos;s journal. Nothing is changed.
          </p>
          {diag?.error && (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{diag.error}</p>
          )}
          {diag?.sections && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                {diag.host} · as {diag.user} · {new Date(diag.ran_at).toLocaleString()}
              </p>
              {diag.sections.map((sec) => (
                <div key={sec.key} className="rounded-md border border-border">
                  <div className="flex items-center gap-2 px-3 py-2 text-sm font-medium">
                    <span className={`inline-block h-2.5 w-2.5 rounded-full ${sec.ok === true ? 'bg-emerald-500' : sec.ok === false ? 'bg-red-500' : 'bg-slate-300'}`} />
                    {sec.title}
                  </div>
                  {sec.hint && (
                    <p className={`px-3 pb-2 text-xs ${sec.ok === false ? 'text-red-700' : 'text-muted-foreground'}`}>{sec.hint}</p>
                  )}
                  <pre className="max-h-72 overflow-auto border-t border-border bg-slate-950 px-3 py-2 font-mono text-[11px] leading-relaxed text-slate-100 whitespace-pre-wrap">
                    {(sec.lines || []).join('\n') || '(no output)'}
                  </pre>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, sub, tone }) {
  const toneCls = tone === 'ok' ? 'text-emerald-600' : tone === 'bad' ? 'text-red-600'
    : tone === 'warn' ? 'text-amber-600' : 'text-foreground';
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="text-xs text-muted-foreground flex items-center gap-1.5"><Icon size={13} /> {label}</div>
      <div className={`text-lg font-semibold mt-1 ${toneCls}`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}
